import CustomDraggable from 'Common/CustomDraggable/CustomDraggable'
import PopupFooter from 'Components/PopupFooter/PopupFooter'
import PopupHeader from 'Components/PopupHeader/PopupHeader'
import ReactPortal from 'Components/ReactPortal/ReactPortal'
import React, { useEffect, useState } from 'react'
import PlanTypeSelector from './PlanTypeSelector'
import PaymentPlanForm from './PaymentPlanForm'
import { LuCalendar } from "react-icons/lu";
import PaymentSchedulePreview from './PaymentSchedulePreview'
import api from 'http'
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage'
import { toast } from 'react-toastify'
import AddNewPaymentCardPopup from 'Popups/AddNewPaymentCardPopup/AddNewPaymentCardPopup'
import Validator, { ValidationTypes as V_Type, } from 'react-form-supervalidator';
import Show from 'Components/Show/Show'
import FullPageLoader from 'Components/FullPageLoader/FullPageLoader'
import isNull from './../../Utils/NullCheck';
import { booleanValidator, installmentValidator, isNullValidator, pastDateCheckValidator } from 'Utils/validators'
import { NO_PATIENT_BALANCE_FOUND, PAYMENT_PLAN_CREATED_SUCCESSFULLY, PLEASE_SELECT_A_PATIENT, PLEASE_SELECT_A_STRIPE_ACCOUNT } from 'Common/ToastConstants'

const CreatePaymentPlanPopup = ({ onClose, disableDraggable, editingPlan, patientId, activeSubTab, showPatientSearch = false }) => {
  const UNIQUE_ID = "CreatePaymentPlanPopup"
  const getDefaultPlanType = () => {
    if (editingPlan?.planType) return editingPlan.planType;
    if (activeSubTab === 'subscriptions') return 'SUB';
    return 'BAL';
  };
  const [planType, setPlanType] = useState(getDefaultPlanType());
  const [selectedPatientId, setSelectedPatientId] = useState(editingPlan?.patientId || patientId || null);
  const [superSearchPatientValue, setSuperSearchPatientValue] = useState('');
  const [formData, setFormData] = useState(
    editingPlan || {
      patientId: selectedPatientId,
      locationId: '',
      planType: getDefaultPlanType(),
      frequency: 'M',
      autoCharge: true,
      gracePeriodInDays: 0,
      paymentMethod: 'card',
      stripeSettingId: '',
      noOfInstallments: 2,
      patientConsent: false,
      isPatientAuthorizationConfirmed: false,
      authorizationMethod: 'digital',
      paymentMethodId: '',
      stripeCustomerId: '',
      firstPaymentDate: '',
      finalPaymentDate: '',
      internalNotes: '',
      totalBalance: 0,
      planDescription: '',
    }
  );
  const [addCardPopup, setAddCardPopup] = useState({
    status: false,
    data: null,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [stripeloader, setStripeLoader] = useState(false);
  const [stripeAccounts, setStripeAccounts] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [creditCardLoader, setCreditCardLoader] = useState(false);
  const [validationModel, setValidationModel] = useState({
    noOfInstallmentsError: null,
    totalBalanceError: null,
    planDescriptionError: null,
    firstPaymentDateError: null,
    locationIdError: null,
    isPatientAuthorizationConfirmedError: null,
    stripeSettingIdError: null,
    recurringAmountError: null,
    finalPaymentDateError: null,
    patientIdError: null,
  })
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      patientId: selectedPatientId,
    }));
  }, [selectedPatientId]);

  const setValidation = () => {
    let validationObj = {
      ...validationModel,
      noOfInstallmentsError: Validator([formData.noOfInstallments,], [installmentValidator], ['Installment must be more then or equal to 2'],),// validate for balance-based and subscription plans
      totalBalanceError: planType === 'BAL' ? Validator([formData.totalBalance,], [isNullValidator], ['Balance due is required for balance-based plans'],) : null,// only validate for balance-based plans
      planDescriptionError: Validator([formData.planDescription,], [V_Type.required], ['Description is required'],),
      firstPaymentDateError: Validator([formData.firstPaymentDate,], [V_Type.required, pastDateCheckValidator], ['Start date is required', 'First payment date should not a past date'],),
      finalPaymentDateError: Validator([formData.firstPaymentDate, formData.finalPaymentDate,], [V_Type.startEndDate], ['Final payment date must be later than first payment date'],),
      locationIdError: Validator([formData.locationId,], [V_Type.required], ['Location is required'],),
      isPatientAuthorizationConfirmedError: Validator([formData.isPatientAuthorizationConfirmed,], [booleanValidator], ['Patient authorization confirmation is required'],),
      stripeSettingIdError: Validator([formData.stripeSettingId,], [V_Type.required], ['Stripe setting is required'],),
      recurringAmountError: planType === 'SUB' ? Validator([formData.recurringAmount,], [isNullValidator], ['Payment Amount is required'],) : null,// only validate for subscription-based plans
      patientIdError: showPatientSearch ? Validator([selectedPatientId,], [V_Type.required], ['Patient is required'],) : null,// only validate if showPatientSearch is true
    }
    setValidationModel(validationObj)
    return Validator(validationObj, V_Type.NullCheck);
  }
  const handleFormChange = async (field, value) => {
    if (field === 'stripeSettingId') {
      const patientIdToUse = selectedPatientId || patientId;
      if (!patientIdToUse) {
        toast.warn(PLEASE_SELECT_A_PATIENT);
        return;
      }
      try {
        setCreditCardLoader(true)
        const resp = await api.get(`${process.env.REACT_APP_TC_PMS}/api/StripeConfiguration/FindStripePatientPaymentMethods/${patientIdToUse}/${value}`);
        let paymentMethodId = '';
        let stripeCustomerId = ''
        if (resp.data.dataModel.patientCardInfo.length > 0) {
          setCreditCards(resp.data.dataModel.patientCardInfo || []);
          paymentMethodId = resp.data.dataModel.patientCardInfo[0].id
          stripeCustomerId = resp.data.dataModel.patientCardInfo[0].customerId
        }
        else {
          setCreditCards([]);
        }
        setFormData(prev => ({
          ...prev,
          stripeSettingId: value,
          paymentMethodId,
          stripeCustomerId
        }));
      } catch (error) {
        ErrorHandlingMessage(error)
      } finally {
        setCreditCardLoader(false)
      }

    }
    else if (field === 'paymentMethodId') {
      // get customer id from creditcard
      const selectedCard = creditCards.find((card) => card.id === value);
      const cusId = selectedCard ? selectedCard.customerId : ''

      setFormData(prev => ({
        ...prev,
        [field]: value,
        stripeCustomerId: cusId
      }));
    }
    else if (field === 'planDescription') {
      if (value.length <= 40) {
        setFormData(prev => ({
          ...prev,
          planDescription: value
        }));
      }
    }
    else if (field === 'internalNotes') {
      if (value.length <= 500) {
        setFormData(prev => ({
          ...prev,
          internalNotes: value
        }));
      }
    }
    else if (field === 'planType' && value === 'SUB') {
      setFormData(prev => ({
        ...prev,
        recurringAmount: 0,
        [field]: value
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

  };

  const handleAddNewCard = async () => {
    if (!formData.stripeSettingId) {
      toast.warn(PLEASE_SELECT_A_STRIPE_ACCOUNT);
      return;
    }
    const patientIdToUse = selectedPatientId || patientId;
    if (!patientIdToUse) {
      toast.warn(PLEASE_SELECT_A_PATIENT);
      return;
    }
    try {

      const response = await api.post(
        `${process.env.REACT_APP_TC_PMS}/api/StripeConfiguration/CreateCheckoutSessionFuturePayment`,
        {
          stripeSettingId: formData.stripeSettingId,
          patientId: patientIdToUse,
        }
      );

      setAddCardPopup({
        status: true,
        data: {
          ...response.data.dataModel,
          publishableKey: stripeAccounts.find((acc) => +acc.id === +formData.stripeSettingId)?.p_Key,
        },
      });
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {

    }
  };
  const closeCardPopup = async () => {
    setAddCardPopup({ status: false, data: null });
    try {
      const patientIdToUse = selectedPatientId || patientId;
      if (!patientIdToUse || !formData.stripeSettingId) return;
      const resp = await api.get(`${process.env.REACT_APP_TC_PMS}/api/StripeConfiguration/FindStripePatientPaymentMethods/${patientIdToUse}/${formData.stripeSettingId}`);
      let paymentMethodId = '';
      let stripeCustomerId = ''
      if (resp.data.dataModel.patientCardInfo.length > 0) {
        setCreditCards(resp.data.dataModel.patientCardInfo || []);
        paymentMethodId = resp.data.dataModel.patientCardInfo[0].id
        stripeCustomerId = resp.data.dataModel.patientCardInfo[0].customerId
      }
      else {
        setCreditCards([]);
      }
      setFormData(prev => ({
        ...prev,
        paymentMethodId,
        stripeCustomerId
      }));
    } catch (error) {

    }
  };
  const handleSavePaymentPlan = async () => {

    let validation = setValidation();
    if (validation) {
      return;
    }

    try {
      setLoader(true)
      const payload = {
        ...formData,
        patientId: selectedPatientId,
      }
      await api.post(`${process.env.REACT_APP_TC_PMS}/api/PatientPaymentPlan/CreatePatientPaymentPlan`, payload);
      toast.success(PAYMENT_PLAN_CREATED_SUCCESSFULLY);
      onClose()
    } catch (error) {
      ErrorHandlingMessage(error)
    } finally {
      setLoader(false)
    }
  }


  useEffect(() => {
    (async () => {
      if (planType === 'SUB') {
        return;
      }

      const patientIdToUse = selectedPatientId || patientId;
      if (!patientIdToUse) {
        setFormData(prev => ({
          ...prev,
          totalBalance: 0,
        }));
        return;
      }
      try {
        setLoader(true);
        const balanceResp = await api.get(`${process.env.REACT_APP_TC_PMS}/api/PatientPaymentPlan/FindPatientBalanceDue?patientId=${patientIdToUse}`);
        if (isNull(balanceResp.data.dataModel)) {
          toast.warn(NO_PATIENT_BALANCE_FOUND);

        }
        setFormData(prev => ({
          ...prev,
          totalBalance: balanceResp.data.dataModel || 0
        }));
      } catch (err) {
        ErrorHandlingMessage(err)
      } finally {
        setLoader(false);
      }
    })();
  }, [selectedPatientId, patientId, planType])
  useEffect(() => {
    (async () => {
      try {
        setStripeLoader(true);
        const res = await api.get(`${process.env.REACT_APP_TC_PMS}/api/StripeSettings/FindStripeSettings`);
        const patientIdToUse = selectedPatientId || patientId;
        let stripeSettingId = ''
        let paymentMethodId = '';
        let stripeCustomerId = ''

        setStripeAccounts(res.data.dataModel);
        if (res.data.dataModel?.length === 1) {
          stripeSettingId = res.data.dataModel[0].id

          // run call to get credit cards
          const creditCardsResp = await api.get(`${process.env.REACT_APP_TC_PMS}/api/StripeConfiguration/FindStripePatientPaymentMethods/${patientIdToUse}/${stripeSettingId}`);
          if (creditCardsResp.data.dataModel.patientCardInfo.length > 0) {
            // firts check if any card is default
            const defaultCard = creditCardsResp.data.dataModel.patientCardInfo.find(card => card.isDefault);
            if (defaultCard) {
              paymentMethodId = defaultCard.id
              stripeCustomerId = defaultCard.customerId
            }
            else {
              paymentMethodId = creditCardsResp.data.dataModel.patientCardInfo[0].id
              stripeCustomerId = creditCardsResp.data.dataModel.patientCardInfo[0].customerId
            }
            setCreditCards(creditCardsResp.data.dataModel.patientCardInfo || []);
          }
          else {
            setCreditCards([]);
          }
        }
        setFormData(prev => ({
          ...prev,
          stripeSettingId: stripeSettingId,
          paymentMethodId: paymentMethodId,
          stripeCustomerId: stripeCustomerId,
        }));
      } catch (err) {
        setStripeAccounts([]);
      } finally {
        setStripeLoader(false);
      }
    })();
  }, [selectedPatientId, patientId])

  const handlePatientSelection = (patient) => {
    if (patient) {
      setSuperSearchPatientValue(patient.name);
      setSelectedPatientId(patient.id);
    } else {
      setSuperSearchPatientValue('');
      setSelectedPatientId(null);
    }
  };

  return (
    <ReactPortal wrapperId={UNIQUE_ID}>
      {addCardPopup.status && (
        <AddNewPaymentCardPopup
          onClose={closeCardPopup}
          data={addCardPopup.data}
          patientId={patientId}
        />
      )}
      {loader && <FullPageLoader allowFullScreen />}
      <div data-te-modal-init className="fixed inset-0 z-10 flex items-center justify-center bg-gray-500 bg-opacity-75" id="exampleModalScrollable" tabIndex={-1} aria-labelledby="exampleModalScrollableLabel" aria-modal="true" data-te-open="true">
        <CustomDraggable disableDraggable={disableDraggable} handle={`#${UNIQUE_ID}`}>
          <div data-te-modal-dialog-ref className="relative w-[1200px]   min-[576px]:mx-auto min-[576px]:mt-2 min-[576px]:max-w-[500px] min-[992px]:max-w-[800px] min-[1200px]:max-w-[1340px] flex flex-col overflow-hidden rounded-md bg-white shadow-lg">
            <div className='p-10 pt-0 '>
              <PopupHeader uniqueId={UNIQUE_ID} heading={editingPlan ? 'Edit Payment Plan' : 'Create Payment Plan'} />
              <div className="flex-1 overflow-y-auto max-h-[calc(85vh-140px)] pr-8">
                <div className="space-y-6 ">
                  {/* Plan Type Selection */}
                  <PlanTypeSelector
                    planType={planType}
                    disabled={!!editingPlan}
                    onChange={(type) => {
                      setPlanType(type);
                      handleFormChange('planType', type);
                    }}
                  />

                  {/* Form */}
                  <PaymentPlanForm
                    planType={planType}
                    formData={formData}
                    onChange={handleFormChange}
                    onAddNewCard={handleAddNewCard}
                    isEditing={!!editingPlan}
                    stripeAccounts={stripeAccounts}
                    creditCards={creditCards}
                    stripeloader={stripeloader}
                    creditCardLoader={creditCardLoader}
                    validationModel={validationModel}
                    showPatientSearch={showPatientSearch}
                    onPatientSelect={handlePatientSelection}
                    superSearchValue={superSearchPatientValue}
                    onSuperSearchValueChange={setSuperSearchPatientValue}
                  />

                  {/* Preview Toggle */}
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="inline-flex items-center text-sm font-medium text-[#604FF8] hover:text-[#604FF8]"
                    >
                      {showPreview ? 'Hide' : 'Show'} Payment Schedule Preview
                      <LuCalendar className="w-4 h-4 ml-2" />
                    </button>
                  </div>

                  {/* Payment Schedule Preview */}
                  <Show when={showPreview}>
                    <PaymentSchedulePreview formData={{ ...formData, recurringAmount: parseFloat(formData.recurringAmount) || 0 }} planType={planType} />
                  </Show>
                </div>
              </div>
              <PopupFooter onClose={onClose} onConfirm={handleSavePaymentPlan} />
            </div>
          </div>
        </CustomDraggable>
      </div>
    </ReactPortal>
  )
}

export default CreatePaymentPlanPopup